export const MODULE_GROUP_KEYS = [
  'chat',
  'communication',
  'tasks_support',
  'sales_marketing',
  'billing_payments',
  'offers_catalog',
  'expenses_management',
  'projects_services',
  'configuration_settings',
  'admin_settings',
  'tools_data_management',
  'reports',
] as const;

/** Groups shown in module management UI (aligned with sidebar). Legacy key offers_catalog kept for API/DB but not displayed. */
export const DISPLAYED_MODULE_GROUP_KEYS: ReadonlyArray<(typeof MODULE_GROUP_KEYS)[number]> = [
  'communication',
  'chat',
  'sales_marketing',
  'tasks_support',
  'projects_services',
  'billing_payments',
  'expenses_management',
  'reports',
  'admin_settings',
  'configuration_settings',
  'tools_data_management',
];

export const MODULE_GROUP_LABELS: Record<(typeof MODULE_GROUP_KEYS)[number], string> = {
  chat: 'Conversations',
  communication: 'Communication',
  tasks_support: 'Work Management & Support',
  sales_marketing: 'CRM',
  billing_payments: 'Finance',
  offers_catalog: 'Offers & Catalog',
  expenses_management: 'Payables',
  projects_services: 'Services & Delivery',
  configuration_settings: 'Configuration',
  admin_settings: 'System & Customization',
  tools_data_management: 'Tools',
  reports: 'Reports',
};

export const MODULE_GROUP_DESCRIPTIONS: Record<(typeof MODULE_GROUP_KEYS)[number], string> = {
  chat: 'CRM conversations linked to tickets and clients',
  communication: 'Reminders and related communication tools',
  tasks_support: 'Tasks, todos, projects, support tickets, and knowledge base',
  sales_marketing: 'Clients, leads, offers, calls, appointments, notes, and marketing',
  billing_payments: 'Client invoices & payments, and suppliers',
  offers_catalog: 'Offers, catalog, and templates (under CRM in sidebar)',
  expenses_management: 'Payables list, payees, payees projection, and recurring payables',
  projects_services: 'Services, packages, support agreements, and client vault',
  configuration_settings: 'Task types, categories, and system parameters',
  admin_settings: 'Users, module management, application settings, file storage, notifications, presets, custom fields, workflows',
  tools_data_management: 'Import invoice, previews (GEMI, 11888), database browser, and activity logs',
  reports: 'Report generator, financial reports, and activity reports',
};

export const moduleManagementConfig = {
  route: '/admin/module-management',
  api: {
    entitlements: '/module-management/entitlements',
  },
};

/** Path prefix to module group key (longest match wins). Used by ModuleGuard. Aligned with new sidebar grouping. */
export const PATH_TO_MODULE_GROUP: Array<{ prefix: string; moduleGroupKey: (typeof MODULE_GROUP_KEYS)[number] }> = [
  { prefix: '/admin/settings/files-storage', moduleGroupKey: 'admin_settings' },
  { prefix: '/admin/settings/system-email-templates', moduleGroupKey: 'admin_settings' },
  { prefix: '/admin/settings/email-notifications', moduleGroupKey: 'admin_settings' },
  { prefix: '/admin/module-management', moduleGroupKey: 'admin_settings' },
  { prefix: '/admin/users', moduleGroupKey: 'admin_settings' },
  { prefix: '/admin/', moduleGroupKey: 'admin_settings' },
  { prefix: '/all-reminders', moduleGroupKey: 'communication' },
  { prefix: '/conversations', moduleGroupKey: 'chat' },
  { prefix: '/support-ticket', moduleGroupKey: 'tasks_support' },
  { prefix: '/crm_tasks', moduleGroupKey: 'tasks_support' },
  { prefix: '/todos', moduleGroupKey: 'tasks_support' },
  { prefix: '/knowledge-base', moduleGroupKey: 'tasks_support' },
  { prefix: '/projects', moduleGroupKey: 'tasks_support' },
  { prefix: '/client-contacts', moduleGroupKey: 'sales_marketing' },
  { prefix: '/leads', moduleGroupKey: 'sales_marketing' },
  { prefix: '/calls', moduleGroupKey: 'sales_marketing' },
  { prefix: '/appointments', moduleGroupKey: 'sales_marketing' },
  { prefix: '/crm_notes', moduleGroupKey: 'sales_marketing' },
  { prefix: '/marketing', moduleGroupKey: 'sales_marketing' },
  { prefix: '/clients', moduleGroupKey: 'sales_marketing' },
  { prefix: '/offers', moduleGroupKey: 'sales_marketing' },
  { prefix: '/offer-templates', moduleGroupKey: 'sales_marketing' },
  { prefix: '/catalog-items', moduleGroupKey: 'sales_marketing' },
  { prefix: '/settings/offer-pdf-layouts', moduleGroupKey: 'sales_marketing' },
  { prefix: '/invoice-payments', moduleGroupKey: 'billing_payments' },
  { prefix: '/client-invoices', moduleGroupKey: 'billing_payments' },
  { prefix: '/suppliers', moduleGroupKey: 'billing_payments' },
  { prefix: '/supplier-invoices', moduleGroupKey: 'billing_payments' },
  { prefix: '/supplier-invoice-payments', moduleGroupKey: 'billing_payments' },
  { prefix: '/payees-projection', moduleGroupKey: 'expenses_management' },
  { prefix: '/payees', moduleGroupKey: 'expenses_management' },
  { prefix: '/payables', moduleGroupKey: 'expenses_management' },
  { prefix: '/recurring-payables', moduleGroupKey: 'expenses_management' },
  { prefix: '/expenses-summary', moduleGroupKey: 'expenses_management' },
  { prefix: '/expenses', moduleGroupKey: 'expenses_management' },
  { prefix: '/expense-categories', moduleGroupKey: 'expenses_management' },
  { prefix: '/p_crm_expense_subcategory', moduleGroupKey: 'expenses_management' },
  { prefix: '/p_crm_expense_category', moduleGroupKey: 'expenses_management' },
  { prefix: '/client-services-yearly', moduleGroupKey: 'projects_services' },
  { prefix: '/service_renewal_reminders', moduleGroupKey: 'projects_services' },
  { prefix: '/client-services', moduleGroupKey: 'projects_services' },
  { prefix: '/support-agreements', moduleGroupKey: 'projects_services' },
  { prefix: '/packages', moduleGroupKey: 'projects_services' },
  { prefix: '/client-vault', moduleGroupKey: 'projects_services' },
  { prefix: '/vault-templates', moduleGroupKey: 'projects_services' },
  { prefix: '/configuration-settings', moduleGroupKey: 'configuration_settings' },
  { prefix: '/p_crm_invoice_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/p_crm_invoice_category', moduleGroupKey: 'configuration_settings' },
  { prefix: '/p_crm_service_category', moduleGroupKey: 'configuration_settings' },
  { prefix: '/app_status', moduleGroupKey: 'configuration_settings' },
  { prefix: '/p_country', moduleGroupKey: 'configuration_settings' },
  { prefix: '/tax_authority', moduleGroupKey: 'configuration_settings' },
  { prefix: '/reminder_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/call_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/ticket_status', moduleGroupKey: 'configuration_settings' },
  { prefix: '/priority', moduleGroupKey: 'configuration_settings' },
  { prefix: '/task_status', moduleGroupKey: 'configuration_settings' },
  { prefix: '/task_category', moduleGroupKey: 'configuration_settings' },
  { prefix: '/task_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/duration', moduleGroupKey: 'configuration_settings' },
  { prefix: '/frequency_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/service_type', moduleGroupKey: 'configuration_settings' },
  { prefix: '/tags', moduleGroupKey: 'configuration_settings' },
  { prefix: '/custom-fields', moduleGroupKey: 'admin_settings' },
  { prefix: '/workflows', moduleGroupKey: 'admin_settings' },
  { prefix: '/activity-logs', moduleGroupKey: 'admin_settings' },
  { prefix: '/notifications', moduleGroupKey: 'admin_settings' },
  { prefix: '/settings', moduleGroupKey: 'admin_settings' },
  { prefix: '/presets-management', moduleGroupKey: 'admin_settings' },
  { prefix: '/tools/gemi-preview', moduleGroupKey: 'tools_data_management' },
  { prefix: '/tools/11888-preview', moduleGroupKey: 'tools_data_management' },
  { prefix: '/tools/database-browser', moduleGroupKey: 'tools_data_management' },
  { prefix: '/import/invoice', moduleGroupKey: 'tools_data_management' },
  { prefix: '/reports/', moduleGroupKey: 'reports' },
  { prefix: '/reports', moduleGroupKey: 'reports' },
];

/** Paths that are never guarded (e.g. module-management so admins can always open it). */
const UNGUARDED_PATHS = ['/dashboard', '/profile-settings', '/preferences'];

export function getModuleGroupKeyForPath(pathname: string): (typeof MODULE_GROUP_KEYS)[number] | null {
  if (UNGUARDED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) return null;
  const sorted = [...PATH_TO_MODULE_GROUP].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const { prefix, moduleGroupKey } of sorted) {
    if (pathname === prefix || (pathname.startsWith(prefix) && (pathname.length === prefix.length || pathname[prefix.length] === '/'))) return moduleGroupKey;
  }
  return null;
}
